import { useState, useCallback } from 'react';
import { message } from 'antd';
import {
  c504Api,
  PendingItem,
  PendingResponse,
  ApproveResponse,
  ApprovalFilter,
  FC120Detail,
} from '../../../services/c504Api';

export interface UseC504ApprovalReturn {
  // State
  loading: boolean;
  approving: boolean;
  rejecting: boolean;
  pendingItems: PendingItem[];
  total: number;
  selectedKeys: string[];
  filter: ApprovalFilter;
  detailData: FC120Detail | null;
  detailLoading: boolean;
  detailVisible: boolean;
  lastApiResponse: unknown;

  // Actions
  setFilter: (f: ApprovalFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchPending: () => Promise<void>;
  batchApprove: () => Promise<ApproveResponse | null>;
  batchReject: () => Promise<ApproveResponse | null>;
  loadDetail: (doNo: string) => Promise<void>;
  closeDetail: () => void;
}

export function useC504Approval(): UseC504ApprovalReturn {
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<ApprovalFilter>({ doStart: '', doEnd: '' });
  const [detailData, setDetailData] = useState<FC120Detail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: PendingResponse = await c504Api.getPending(filter);
      setLastApiResponse(resp);
      setPendingItems(resp.items || []);
      setTotal(resp.total || 0);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadDetail = useCallback(async (doNo: string) => {
    setDetailLoading(true);
    setDetailVisible(true);
    try {
      const data = await c504Api.getDetail(doNo);
      setLastApiResponse(data);
      setDetailData(data);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '取得明細失敗');
      message.error(msg);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetailVisible(false);
    setDetailData(null);
  }, []);

  const batchApprove = useCallback(async (): Promise<ApproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆交運單進行核准');
      return null;
    }
    setApproving(true);
    try {
      const resp = await c504Api.approve(selectedKeys);
      message.success(resp.message);
      await fetchPending();
      setLastApiResponse(resp);
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
      message.error(msg);
      return null;
    } finally {
      setApproving(false);
    }
  }, [selectedKeys, fetchPending]);

  const batchReject = useCallback(async (): Promise<ApproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆交運單進行駁回');
      return null;
    }
    setRejecting(true);
    try {
      const resp = await c504Api.reject(selectedKeys);
      message.success(resp.message);
      await fetchPending();
      setLastApiResponse(resp);
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '駁回失敗');
      message.error(msg);
      return null;
    } finally {
      setRejecting(false);
    }
  }, [selectedKeys, fetchPending]);

  return {
    loading,
    approving,
    rejecting,
    pendingItems,
    total,
    selectedKeys,
    filter,
    detailData,
    detailLoading,
    detailVisible,
    lastApiResponse,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
    batchReject,
    loadDetail,
    closeDetail,
  };
}
